import { useContext, createContext } from 'react';
import { formUtils } from '@insurancemenu/imenu-components-lib';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types';
import { getDomainProductLine } from 'api/operations/domainServiceV2';
import { INITIAL_FORM_OBJECT, requestFormById } from 'api/operations/formService';
import { getFullProductTemplateInfo, saveAnswerAndCompleteStep, saveAnswers } from 'api/operations/productServiceV2';
import { runInAction, toJS, makeAutoObservable } from 'mobx';
import { RootStore, getRootStoreInstance } from 'store/rootStore';
import { ProductTemplate, ProductTemplateChecklistCard } from 'types/productServiceV2';

export default class RFQPolicyAttributesFormStore {
  rootStore: RootStore;

  _isLoading = false;
  _form: FormObjectFromJSON;
  _dynamicForm: null | formUtils.DynamicFormController;
  _productTemplate: ProductTemplate | null = null;
  _isWarningModalOpen = false;

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get form(): FormObjectFromJSON {
    return this._form;
  }

  public get dynamicForm(): null | formUtils.DynamicFormController {
    return this._dynamicForm;
  }

  public get productTemplate(): ProductTemplate | null {
    return this._productTemplate;
  }

  public get isWarningModalOpen(): boolean {
    return this._isWarningModalOpen;
  }

  private setLoading(loadingStatus: boolean): void {
    this._isLoading = loadingStatus;
  }

  private setProductTemplate(productTemplate: ProductTemplate | null): void {
    this._productTemplate = productTemplate;
  }

  private setDynamicForm(dynamicForm: null | formUtils.DynamicFormController): void {
    this._dynamicForm = dynamicForm;
  }

  private setForm(form: FormObjectFromJSON): void {
    this._form = form;
  }

  public setWarningModalStatus(status: boolean): void {
    this._isWarningModalOpen = status;
  }

  public async getInitialData(templateId: string): Promise<void> {
    this.setLoading(true);
    const res = await getFullProductTemplateInfo(templateId);
    await runInAction(async () => {
      if (res) {
        this.setProductTemplate(res);

        // Get domain product lines to get policy attributes form id
        const domainPLines = await getDomainProductLine({
          effectiveDate: res.effectiveFrom,
          zipCode: '',
          state: res.filterBC?.state?.[0] || '',
          county: '',
          agencyId: '',
          sponsorId: this.rootStore.userStore.sponsorId,
        });

        const policyAttributesFormId =
          domainPLines.find(
            (pLine) => pLine.productLineId === res.productLineId && pLine.rateSegment === res.rateSegment,
          )?.policyAttributesFormId || '';

        if (policyAttributesFormId) await this.getPolicyAttributesForm(policyAttributesFormId);
        this.setLoading(false);
      }
    });
  }

  private async getPolicyAttributesForm(formId: string): Promise<void> {
    this.setLoading(true);
    this.setDynamicForm(null);
    this.setForm(INITIAL_FORM_OBJECT);
    const form = await requestFormById(formId);
    runInAction(() => {
      this.setForm(form);
      const convertedTemplate = toJS(this.productTemplate);

      this.createFormController(convertedTemplate ? { ...convertedTemplate?.formAnswers?.policyAttributes } : {});
      this.setLoading(false);
    });
  }

  private createFormController(answers: Record<string, string>): void {
    this.setDynamicForm(new formUtils.DynamicFormController(this.form, this.rootStore.userStore.userRoles));

    this.dynamicForm?.populateFields(answers);
  }

  public async saveAnswers(completesStep: boolean): Promise<void> {
    this.setLoading(true);

    if (!this.productTemplate) return;

    const currentTemplate = { ...toJS(this.productTemplate) };

    if (currentTemplate?.formAnswers?.policyAttributes && this.dynamicForm?.fields) {
      Object.entries(this.dynamicForm.getCanonicalAnswers()).forEach(([key, value]) => {
        currentTemplate.formAnswers.policyAttributes[key] = String(value) || '';
      });
    }

    const { id, formAnswers } = currentTemplate;
    if (completesStep) {
      await saveAnswerAndCompleteStep(id, formAnswers, ProductTemplateChecklistCard.POLICY_ATTRIBUTES);
    } else {
      await saveAnswers(id, formAnswers);
    }
    this.setLoading(false);
  }

  public resetInitialData(): void {
    this.setDynamicForm(null);
    this.setProductTemplate(null);
    this.setForm(INITIAL_FORM_OBJECT);
    this.setWarningModalStatus(false);
  }

  constructor() {
    this.rootStore = getRootStoreInstance();
    this._dynamicForm = null;
    this._form = INITIAL_FORM_OBJECT;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const PageStoreContext = createContext(new RFQPolicyAttributesFormStore());

export const useRFQPolicyAttributesFormStore = (): RFQPolicyAttributesFormStore => {
  return useContext(PageStoreContext);
};

export { PageStoreContext };
